'use client';

import { useTranslation } from 'react-i18next';
import type { CommerceAvailability } from '@akademi/site-builder-runtime';
import { sharedStyles } from '@akademi/site-builder-blocks';

const { badgeAmber, badgeBase, badgeError, badgeSuccess } = sharedStyles;

/**
 * Coarse stock badge shared by {@code ProductGrid} and {@code ProductDetail}.
 * Deliberately renders only the state {@link CommerceAvailability} carries
 * ("in stock" / "low stock" / …) — never a quantity. Showing "3 left" on a
 * public page is a business decision the backend hasn't made here, not a
 * rendering one this block should invent.
 */
export function AvailabilityBadge({ availability }: { availability?: CommerceAvailability }) {
  const { t } = useTranslation('site-builder-blocks');
  if (!availability) return null;

  switch (availability) {
    case 'IN_STOCK':
      return <span style={badgeSuccess}>{t('commerce.availability.inStock')}</span>;
    case 'LOW_STOCK':
      return <span style={badgeAmber}>{t('commerce.availability.lowStock')}</span>;
    case 'PREORDER':
      return (
        <span
          style={{
            ...badgeBase,
            background: 'var(--color-brand-500, #319db3)',
            color: 'var(--color-text-on-brand, #fff)',
          }}
        >
          {t('commerce.availability.preorder')}
        </span>
      );
    case 'BACKORDER':
      return <span style={badgeAmber}>{t('commerce.availability.backorder')}</span>;
    case 'OUT_OF_STOCK':
      return <span style={badgeError}>{t('commerce.availability.outOfStock')}</span>;
    default:
      return null;
  }
}
